import 'moment/locale/fr';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { faCheck, faPlus } from '@fortawesome/free-solid-svg-icons';

import useGetRequest from '../../api/useGetRequest';
import { buildUrlWithParams } from '../../utils/url.utils';

import GenericPageContainer from '../../components/agv-generic-page-container';
import SearchFilter from '../../components/agv-header-search';
import GenericTable from '../../components/agv-generic-table';
import GenericTableSkeleton from '../../components/agv-generic-table/genericTableSkeleton';
import EmptyList from '../../components/agv-empty-list';
import AgrivitechPagination from '../../components/agv-pagination';
import { STATUS_ENUM } from '../../components/agv-status/status-enum';
import './reserve-reasons.css';
import { useSelector } from 'react-redux';
import { RESERVE_REASONS_ICONS_DISPLAY } from '../../enums/reserve-reasons-enum.enum';
import { returnCriticalityDiv } from '../../utils/criticalities';
import HeaderButton from '../../components/agv-header-button';
import ReserveReasonCreateModal from './reserve-reasons-create-modal';
import useCreateReserveReason from '../../api/reserve-reasons/useCreateReserveReason';
import useUpdateReserveReason from '../../api/reserve-reasons/useUpdateReserveReason';
import ReserveReasonUpdateModal from './reserve-reasons-update-modal';
import useArchiveReserveReason from '../../api/reserve-reasons/useArchiveReserveReason';

export default function ReserveReasons() {
  const [reserveReason, setReserveReason] = useState(null);
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [isOpenUpdate, setIsOpenUpdate] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const features = useSelector((state) => state.features);

  const [filters, setFilters] = useState({
    limit: 10,
    offset: searchParams.get('offset') || 0,
    query: searchParams.get('query') || '',
  });

  const handleUpdateParam = useCallback(
    (key, value) => {
      setFilters((prevFilters) => {
        const newFilters = { ...prevFilters, [key]: value };

        if (key !== 'offset') {
          newFilters.offset = 0;
        }

        return newFilters;
      });
    },
    [navigate]
  );
  const url = useMemo(
    () => buildUrlWithParams('/reserve-reasons', filters),
    [filters]
  );

  const { data, isLoading, mutate } = useGetRequest(url, 300);

  const {
    createReserveReason,
    isLoading: isLoadingCreateReserveReason,
    isSuccess: isSuccessCreateReserveReason,
  } = useCreateReserveReason();

  const {
    updateReserveReason,
    isLoading: isLoadingUpdateReserveReason,
    isSuccess: isSuccessUpdateReserveReason,
  } = useUpdateReserveReason();

  const {
    archiveReserveReason,
    isLoading: isLoadingArchiveReserveReason,
    isSuccess: isSuccessArchiveReserveReason,
  } = useArchiveReserveReason();

  useEffect(() => {
    const params = new URLSearchParams(filters);
    navigate({ search: params.toString() }, { replace: true });
  }, [filters, navigate]);

  useEffect(() => {
    if (isSuccessCreateReserveReason) {
      setIsOpenCreate(false);
      mutate();
    }
  }, [isSuccessCreateReserveReason]);

  useEffect(() => {
    if (isSuccessUpdateReserveReason || isSuccessArchiveReserveReason) {
      setIsOpenUpdate(false);
      mutate();
    }
  }, [isSuccessUpdateReserveReason, isSuccessArchiveReserveReason]);

  const handleReasonReservesFields = (reason) => {
    const fields = [];

    const deliveryTypes = reason?.types || [];
    const deliveryTypesText =
      deliveryTypes.length > 0 ? deliveryTypes.join(', ') : '-';

    fields.push({
      name: 'Types',
      value: deliveryTypesText,
    });

    fields.push({
      name: 'Criticité',
      value: reason?.criticality
        ? returnCriticalityDiv(reason?.criticality)
        : '-',
    });

    return fields;
  };

  const onOpenModal = (value) => {
    setReserveReason(value);
    setIsOpenUpdate(true);
  };

  return (
    <>
      <GenericPageContainer
        title={"Gestion des catégories d'anomalies"}
        subtitle={"Retrouvez la liste de vos raisons d'anomalies."}
        filters={
          <>
            <SearchFilter
              value={filters.query}
              setValue={(value) => handleUpdateParam('query', value)}
              disabled={isLoading}
            />
            <HeaderButton
              onClick={() => setIsOpenCreate(true)}
              text={'Créer'}
              icon={faPlus}
            />
          </>
        }
      >
        {isLoading ? (
          <GenericTableSkeleton numberOfRows={10} hasTitle />
        ) : data?.items?.length > 0 ? (
          <GenericTable
            isLoading={isLoading}
            rows={data.items.map((element) => ({
              title: element.reason_message || 'Aucun titre',
              subtitle: element?.description || '-',
              icon: RESERVE_REASONS_ICONS_DISPLAY[element?.icon] ?? faCheck,
              onClick: () => onOpenModal(element),
              buttonText: 'Détails',
              status: element?.is_archived
                ? STATUS_ENUM.NEGATIVE
                : STATUS_ENUM.POSITIVE,
              statusText: element?.is_archived ? 'Archivée' : 'Actif',
              fields: handleReasonReservesFields(element),
            }))}
            iconColor={'tw-text-checklist-common'}
          />
        ) : (
          <EmptyList />
        )}
        <AgrivitechPagination
          data={data}
          setOffset={(value) => handleUpdateParam('offset', value)}
        />
      </GenericPageContainer>
      <ReserveReasonCreateModal
        isOpen={isOpenCreate}
        setIsOpen={setIsOpenCreate}
        createReserveReason={createReserveReason}
        mainActionIsLoading={isLoadingCreateReserveReason}
      />
      <ReserveReasonUpdateModal
        isOpen={isOpenUpdate}
        setIsOpen={setIsOpenUpdate}
        updateReserveReason={updateReserveReason}
        archiveReserveReason={archiveReserveReason}
        mainActionIsLoading={isLoadingUpdateReserveReason}
        reserveReason={reserveReason}
        isLoadingArchiveReserveReason={isLoadingArchiveReserveReason}
      />
    </>
  );
}
